import type { GameMode } from './logic/types';
import { MODE_NAMES } from './logic/challenges';

const KEYS = {
  highScore: 'sage-stack.high-score',
  sound: 'sage-stack.sound',
  reducedMotion: 'sage-stack.reduced-motion',
  instructionsViewed: 'sage-stack.instructions-viewed',
  mode: 'sage-stack.last-mode',
  music: 'sage-stack.music',
  volume: 'sage-stack.volume',
  scenery: 'sage-stack.scenery',
  haptics: 'sage-stack.haptics',
  quietInterval: 'sage-stack.quiet-interval',
  puzzleIndex: 'sage-stack.puzzle-index',
} as const;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // The game remains fully playable when storage is unavailable.
  }
}

export const preferences = {
  getQuietInterval: (): number => {
    const n = Number(read(KEYS.quietInterval));
    return [850, 1500, 2500].includes(n) ? n : 1500;
  },
  setQuietInterval: (n: number): void => write(KEYS.quietInterval, String(n)),
  getPuzzleIndex: (): number =>
    Math.max(0, Math.min(11, Number.parseInt(read(KEYS.puzzleIndex) ?? '0', 10) || 0)),
  setPuzzleIndex: (n: number): void => write(KEYS.puzzleIndex, String(n)),
  getMusic: (): boolean => read(KEYS.music) !== 'false',
  setMusic: (enabled: boolean): void => write(KEYS.music, String(enabled)),
  getVolume: (): number => {
    const volume = Number(read(KEYS.volume) ?? '0.55');
    return Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 0.55;
  },
  setVolume: (volume: number): void => write(KEYS.volume, String(volume)),
  getScenery: (): boolean => read(KEYS.scenery) !== 'false',
  setScenery: (enabled: boolean): void => write(KEYS.scenery, String(enabled)),
  getHaptics: (): boolean => read(KEYS.haptics) === 'true',
  setHaptics: (enabled: boolean): void => write(KEYS.haptics, String(enabled)),
  getHighScore: (): number => Number.parseInt(read(KEYS.highScore) ?? '0', 10) || 0,
  setHighScore: (score: number): void => write(KEYS.highScore, String(Math.max(0, score))),
  getSound: (): boolean => read(KEYS.sound) === 'true',
  setSound: (enabled: boolean): void => write(KEYS.sound, String(enabled)),
  getReducedMotion: (): boolean => {
    const stored = read(KEYS.reducedMotion);
    return stored === null
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : stored === 'true';
  },
  setReducedMotion: (enabled: boolean): void => write(KEYS.reducedMotion, String(enabled)),
  getInstructionsViewed: (): boolean => read(KEYS.instructionsViewed) === 'true',
  setInstructionsViewed: (): void => write(KEYS.instructionsViewed, 'true'),
  getMode: (): GameMode => {
    const value = read(KEYS.mode);
    return value && Object.hasOwn(MODE_NAMES, value) ? (value as GameMode) : 'growing-season';
  },
  setMode: (mode: GameMode): void => write(KEYS.mode, mode),
};
